import {
  useCallback,
  useRef,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from "react";
import type { DraftPair } from "../../challenges/types/challengeDraft";
import {
  createPairId,
  normalizeCpf,
} from "../../challenges/utils/challengePairs";
import type { TournamentPairModalState } from "../../tournaments/types/tournamentAdmin";
import type { AvailabilityRestrictionPeriod } from "../../tournamentAvailability/tournamentAvailabilitySettings";
import {
  createManualEventRegistration,
  updateEventRegistrationPair,
  type AdminEventRegistration,
} from "../services/eventRegistrationAdminService";
import type { TournamentRegistrationCategoryItem } from "../utils/tournamentCategories";

type TournamentPairCapacity = {
  exceeds: boolean;
  limit: number;
};

type UseAdminTournamentPairModalActionsInput = {
  activeRegistrationCategoryKeyForModal: string;
  adminPaymentMethod: "pix" | "credit_card" | "debit_card";
  autoProvisionInFlightRef: MutableRefObject<Set<string>>;
  challengeDetailId?: number;
  challengeEventRegistrations: AdminEventRegistration[];
  challengeTournamentCategories: TournamentRegistrationCategoryItem[];
  availabilityRestrictionPeriods: AvailabilityRestrictionPeriod[];
  getTournamentPairCapacityForCategory: (
    categoryKey: string,
    editingRegistration?: AdminEventRegistration | null,
  ) => TournamentPairCapacity;
  loadChallengeEventRegistrations: (eventId?: string | null) => Promise<void>;
  loadEventRegistrations: () => Promise<void>;
  manualRegistrationEventIdForChallenge: string;
  nameByCpf: Record<string, string>;
  removedTournamentPairKeysRef: MutableRefObject<Set<string>>;
  resolveChallengePlayerCpfInput: (value: string) => string;
  setDraftPairs: Dispatch<SetStateAction<DraftPair[]>>;
  setParticipantsFromPairs: (nextPairs: DraftPair[]) => void;
  setTournamentPairModal: Dispatch<SetStateAction<TournamentPairModalState | null>>;
  setTournamentPairModalSaving: Dispatch<SetStateAction<boolean>>;
  showErrorToast: (message: string) => void;
  showSuccessToast: (message: string) => void;
  token?: string | null;
  tournamentPairModal: TournamentPairModalState | null;
  tournamentPairModalSaving: boolean;
};

export function useAdminTournamentPairModalActions({
  activeRegistrationCategoryKeyForModal,
  adminPaymentMethod,
  autoProvisionInFlightRef,
  challengeDetailId,
  challengeEventRegistrations,
  challengeTournamentCategories,
  availabilityRestrictionPeriods,
  getTournamentPairCapacityForCategory,
  loadChallengeEventRegistrations,
  loadEventRegistrations,
  manualRegistrationEventIdForChallenge,
  nameByCpf,
  removedTournamentPairKeysRef,
  resolveChallengePlayerCpfInput,
  setDraftPairs,
  setParticipantsFromPairs,
  setTournamentPairModal,
  setTournamentPairModalSaving,
  showErrorToast,
  showSuccessToast,
  token,
  tournamentPairModal,
  tournamentPairModalSaving,
}: UseAdminTournamentPairModalActionsInput) {
  const submitInFlightRef = useRef(false);

  const openTournamentPairModal = useCallback(
    (registration?: AdminEventRegistration | null) => {
      const formatPlayerInput = (nameValue: unknown, cpfValue: unknown) => {
        const cpf = normalizeCpf(cpfValue);
        if (!cpf) return "";
        const name =
          String(nameValue || "").trim() || String(nameByCpf[cpf] || "").trim();
        return name ? `${name} - ${cpf}` : cpf;
      };
      const categoryKey =
        String(registration?.registrationCategoryKey || "").trim() ||
        activeRegistrationCategoryKeyForModal ||
        String(challengeTournamentCategories[0]?.categoryKey || "").trim();
      setTournamentPairModal({
        mode: registration ? "edit" : "create",
        registration: registration || null,
        cpf1: formatPlayerInput(registration?.userName, registration?.userCpf),
        cpf2: formatPlayerInput(
          registration?.partnerName,
          registration?.partnerCpf,
        ),
        categoryKey,
        availabilityRestrictionPeriodKeys:
          registration?.availabilityRestriction?.details
            ?.map((restriction) =>
              [
                String(restriction.label || "").trim(),
                String(restriction.startAt || "").trim(),
                String(restriction.endAt || "").trim(),
              ].join("|"),
            )
            .filter(Boolean) || [],
        shirtSizes: {
          player: String(registration?.shirtSizes?.player || ""),
          partner: String(registration?.shirtSizes?.partner || ""),
        },
      });
    },
    [
      activeRegistrationCategoryKeyForModal,
      challengeTournamentCategories,
      nameByCpf,
      setTournamentPairModal,
    ],
  );

  const closeTournamentPairModal = useCallback(() => {
    if (tournamentPairModalSaving) return;
    setTournamentPairModal(null);
  }, [setTournamentPairModal, tournamentPairModalSaving]);

  const submitTournamentPairModal = useCallback(async () => {
    if (!token || !tournamentPairModal || submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    const eventId = String(manualRegistrationEventIdForChallenge || "").trim();
    if (!eventId) {
      submitInFlightRef.current = false;
      showErrorToast("Salve o torneio antes de adicionar duplas.");
      return;
    }
    const cpf1 = resolveChallengePlayerCpfInput(tournamentPairModal.cpf1);
    const cpf2 = resolveChallengePlayerCpfInput(tournamentPairModal.cpf2);
    const categoryKey = String(tournamentPairModal.categoryKey || "").trim();
    if (!categoryKey) {
      submitInFlightRef.current = false;
      showErrorToast("Selecione a categoria da dupla.");
      return;
    }
    if (!cpf1 || !cpf2 || cpf1.length < 8 || cpf2.length < 8) {
      submitInFlightRef.current = false;
      showErrorToast("Selecione dois jogadores com CPF válido.");
      return;
    }
    if (cpf1 === cpf2) {
      submitInFlightRef.current = false;
      showErrorToast("A dupla precisa ter dois jogadores diferentes.");
      return;
    }
    const editingRegistration = tournamentPairModal.registration || null;
    const selectedShirtSizes = {
      player: String(tournamentPairModal.shirtSizes?.player || "").trim(),
      partner: String(tournamentPairModal.shirtSizes?.partner || "").trim(),
    };
    const availabilityPeriodKey = (period: AvailabilityRestrictionPeriod) =>
      [
        String(period.label || "").trim(),
        String(period.startAt || "").trim(),
        String(period.endAt || "").trim(),
      ].join("|");
    const selectedAvailabilityRestrictionKeys = new Set(
      tournamentPairModal.availabilityRestrictionPeriodKeys || [],
    );
    const selectedAvailabilityRestrictionPeriods =
      availabilityRestrictionPeriods.filter((period) =>
        selectedAvailabilityRestrictionKeys.has(availabilityPeriodKey(period)),
      );
    const availabilityRestrictionPayload =
      selectedAvailabilityRestrictionPeriods.length > 0
        ? {
            enabled: true,
            periodKeys: selectedAvailabilityRestrictionPeriods.map((period) =>
              availabilityPeriodKey(period),
            ),
            periods: selectedAvailabilityRestrictionPeriods.map((period) => ({
              periodKey: availabilityPeriodKey(period),
              label: period.label,
              startAt: period.startAt,
              endAt: period.endAt,
              type: period.type,
            })),
          }
        : { enabled: false };
    const previousCpf1 = normalizeCpf(editingRegistration?.userCpf);
    const previousCpf2 = normalizeCpf(editingRegistration?.partnerCpf);
    const previousPairId =
      previousCpf1.length >= 8 &&
      previousCpf2.length >= 8 &&
      previousCpf1 !== previousCpf2
        ? createPairId(previousCpf1, previousCpf2)
        : "";
    const previousCategoryKey = String(
      editingRegistration?.registrationCategoryKey || categoryKey,
    ).trim();
    const tournamentIdForPairKey = Number(
      editingRegistration?.tournamentId || challengeDetailId || 0,
    );
    const capacity = getTournamentPairCapacityForCategory(
      categoryKey,
      editingRegistration,
    );
    if (capacity.exceeds) {
      submitInFlightRef.current = false;
      showErrorToast(
        `Categoria atingiu o limite de ${capacity.limit} inscritos.`,
      );
      return;
    }

    const duplicateInSameCategory = challengeEventRegistrations.some((row) => {
      if (row.status === "canceled") return false;
      if (Number(row.id) === Number(editingRegistration?.id || 0)) {
        return false;
      }
      if (String(row.registrationCategoryKey || "").trim() !== categoryKey) {
        return false;
      }
      const rowCpfs = [normalizeCpf(row.userCpf), normalizeCpf(row.partnerCpf)];
      return rowCpfs.includes(cpf1) || rowCpfs.includes(cpf2);
    });
    if (duplicateInSameCategory) {
      submitInFlightRef.current = false;
      showErrorToast("Um dos jogadores já está inscrito nesta categoria.");
      return;
    }

    setTournamentPairModalSaving(true);
    try {
      if (tournamentPairModal.mode === "edit" && editingRegistration) {
        await updateEventRegistrationPair(token, editingRegistration.id, {
          cpf: cpf1,
          teammateCpf: cpf2,
          registrationCategoryKey: categoryKey,
          shirtSizes: selectedShirtSizes,
          allowEligibilityOverride: true,
          overrideReason: "admin_pair_category_override",
        });
      } else {
        await createManualEventRegistration(token, eventId, {
          cpf: cpf1,
          participantMode: "dupla_fixa",
          teammateCpf: cpf2,
          registrationCategoryKey: categoryKey,
          shirtSizes: selectedShirtSizes,
          availabilityRestriction: availabilityRestrictionPayload,
          confirmPayment: false,
          paymentMethod: adminPaymentMethod,
          adminNote: "manual-admin",
          allowEligibilityOverride: true,
          overrideReason: "admin_pair_category_override",
        });
      }
      const savedPairId = createPairId(cpf1, cpf2);
      if (
        previousPairId &&
        (previousPairId !== savedPairId || previousCategoryKey !== categoryKey)
      ) {
        removedTournamentPairKeysRef.current.add(
          `${tournamentIdForPairKey}::${previousCategoryKey}::${previousPairId}`,
        );
        autoProvisionInFlightRef.current.delete(
          `pair:${previousCategoryKey || "sem-categoria"}:${previousPairId}`,
        );
        setDraftPairs((prev) => {
          const next = prev.filter((pair) => pair.id !== previousPairId);
          if (next.length !== prev.length) {
            setParticipantsFromPairs(next);
          }
          return next;
        });
      }
      removedTournamentPairKeysRef.current.delete(
        `${Number(challengeDetailId || 0)}::${categoryKey}::${savedPairId}`,
      );
      autoProvisionInFlightRef.current.delete(
        `pair:${categoryKey || "sem-categoria"}:${savedPairId}`,
      );
      await loadEventRegistrations();
      await loadChallengeEventRegistrations(eventId);
      setTournamentPairModal(null);
      showSuccessToast(
        tournamentPairModal.mode === "edit"
          ? "Dupla atualizada."
          : "Dupla adicionada.",
      );
    } catch (err) {
      showErrorToast(
        err instanceof Error ? err.message : "Não foi possível salvar a dupla.",
      );
    } finally {
      setTournamentPairModalSaving(false);
      submitInFlightRef.current = false;
    }
  }, [
    adminPaymentMethod,
    availabilityRestrictionPeriods,
    autoProvisionInFlightRef,
    challengeDetailId,
    challengeEventRegistrations,
    getTournamentPairCapacityForCategory,
    loadChallengeEventRegistrations,
    loadEventRegistrations,
    manualRegistrationEventIdForChallenge,
    removedTournamentPairKeysRef,
    resolveChallengePlayerCpfInput,
    setDraftPairs,
    setParticipantsFromPairs,
    setTournamentPairModal,
    setTournamentPairModalSaving,
    showErrorToast,
    showSuccessToast,
    token,
    tournamentPairModal,
  ]);

  return {
    closeTournamentPairModal,
    openTournamentPairModal,
    submitTournamentPairModal,
  };
}
